import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { createDatepicker } from '@/js/modules/datepicker';

export default class extends Controller {
  static targets = [
    'durationSelect',
    'customRange',
    'fromDate',
    'toDate',
    'hoursDisplay',
    'submitButton',
  ];
  static values = { hoursUrl: String, createUrl: String, sectionId: String };
  static outlets = ['billable-tax'];

  connect() {
    this.pendingFetch = null;
    this.datepickers = [this.fromDateTarget, this.toDateTarget].map((field) =>
      createDatepicker(field, { format: 'yyyy-mm-dd' })
    );

    this.element.addEventListener('changeDate', this.#dateChanged);
  }

  disconnect() {
    this.pendingFetch?.abort();
    this.pendingFetch = null;
    this.element.removeEventListener('changeDate', this.#dateChanged);
    this.datepickers.forEach((datepicker) => datepicker.destroy());
    this.datepickers = [];
  }

  scopeChanged(event) {
    if (event.target.value === 'all_uninvoiced') {
      this.durationSelectTarget.disabled = true;
      this.customRangeTarget.classList.add('hide');
      this.#fetchHours('all_uninvoiced');
    } else {
      this.durationSelectTarget.disabled = false;
      this.durationChanged();
    }
  }

  durationChanged() {
    const duration = this.durationSelectTarget.value;
    this.customRangeTarget.classList.toggle('hide', duration !== 'custom');

    if (duration !== 'custom') {
      this.#fetchHours(duration);
    } else if (this.#hasCustomRange()) {
      this.#fetchHours(this.#customRange());
    } else {
      this.pendingFetch?.abort();
      this.#renderHours('');
      this.fromDateTarget.focus();
    }
  }

  #dateChanged = () => {
    if (this.#hasCustomRange()) {
      this.#fetchHours(this.#customRange());
    }
  };

  #customRange() {
    return [this.fromDateTarget.value, this.toDateTarget.value];
  }

  #hasCustomRange() {
    return this.#customRange().every(Boolean);
  }

  async submit() {
    const button = this.submitButtonTarget;
    if (button.disabled) {
      return;
    }

    const body = new FormData();
    this.#appendDuration(body, this.#selectedDuration());
    body.append('tax', this.#taxAllChecked());

    button.disabled = true;
    button.classList.add('button-loading');

    try {
      const request = new FetchRequest('post', this.createUrlValue, {
        body,
        responseKind: 'turbo-stream',
      });

      const response = await request.perform();
      if (!response.ok) {
        throw new Error(`Request failed: ${response.statusCode}`);
      }

      this.dispatch('request-close', {
        prefix: 'turbo-modal',
        target: this.element.closest('[data-controller~="ui--turbo-modal"]'),
      });
    } catch (error) {
      console.error(error);
    } finally {
      button.disabled = false;
      button.classList.remove('button-loading');
    }
  }

  async #fetchHours(duration) {
    this.pendingFetch?.abort();
    const { signal } = (this.pendingFetch = new AbortController());

    this.#renderHours('');

    const url = new URL(this.hoursUrlValue, window.location.origin);
    this.#appendDuration(url.searchParams, duration);

    try {
      const request = new FetchRequest('get', url.toString(), { responseKind: 'json', signal });
      const response = await request.perform();
      const data = await response.json;
      if (signal.aborted) {
        return;
      }
      this.#renderHours(data.billable_hours);
    } catch (error) {
      if (signal.aborted) {
        return;
      }
      console.error(error);
    }
  }

  #renderHours(value) {
    this.hoursDisplayTarget.textContent = value;
    this.submitButtonTarget.disabled = value === '' || value === '00:00';
  }

  #selectedDuration() {
    if (this.element.querySelector('#tracked-hours-all-uninvoiced').checked) {
      return 'all_uninvoiced';
    }

    const duration = this.durationSelectTarget.value;
    if (duration === 'custom') {
      return this.#customRange();
    }
    return duration;
  }

  #appendDuration(params, duration) {
    if (Array.isArray(duration)) {
      duration.forEach((date) => params.append('duration[]', date));
    } else {
      params.append('duration', duration);
    }
  }

  #taxAllChecked() {
    return this.hasBillableTaxOutlet ? this.billableTaxOutlet.allItemsTaxable : false;
  }
}
